// Sign in / create account.
//
// Three paths, all landing on the same JWT: email + password, Google (a full-page redirect the
// Worker completes), and an invite code during the closed beta. Sign in with Apple is intentionally
// absent for now — the Worker pins the Apple token audience to the iOS bundle id, so the web flow
// needs a separate Services ID before it can work.

import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import { googleSignInURL, requestPasswordReset } from '../api/auth';
import { ApiError } from '../api/client';
import { Field, Notice, PrimaryButton } from '../components/ui';
import { OreiaGlyph } from '../components/Icons';

type Mode = 'signin' | 'signup' | 'forgot';

export function SignInScreen() {
  const { signIn, signUp } = useSession();
  const [params] = useSearchParams();

  const [mode, setMode] = useState<Mode>(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState(params.get('invite') ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentReset, setSentReset] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (mode === 'forgot') {
        await requestPasswordReset(email);
        setSentReset(true);
      } else if (mode === 'signup') {
        await signUp({
          email,
          password,
          name: name.trim() || undefined,
          inviteCode: inviteCode.trim() || undefined,
        });
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  }

  const heading =
    mode === 'signup' ? 'Begin your journal.' : mode === 'forgot' ? 'Reset your password.' : 'Welcome back.';

  return (
    <div className="oscreen">
      <div className="oscroll">
        <div className="ocolumn opane">
          <div className="orow" style={{ gap: 10, marginBottom: 34 }}>
            <OreiaGlyph style={{ width: 26, height: 26, color: 'var(--ink)' }} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 29, lineHeight: 1 }}>Oreia</span>
          </div>

          <h1 className="oserif" style={{ fontSize: 42, margin: '0 0 10px' }}>
            {heading}
          </h1>
          <p className="otext-soft" style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 28px', maxWidth: '34ch' }}>
            {mode === 'signup'
              ? 'Your dreams and reflections, encrypted with a key only you can unlock.'
              : mode === 'forgot'
                ? "We'll email you a link to choose a new password."
                : 'Sign in to continue where you left off.'}
          </p>

          {sentReset ? (
            <div className="ostack">
              <Notice>
                Check <strong>{email}</strong> for a reset link. It expires in an hour.
              </Notice>
              <button className="octa quiet" onClick={() => { setMode('signin'); setSentReset(false); }}>
                Back to sign in
              </button>
            </div>
          ) : (
            <form className="ostack" style={{ gap: 16 }} onSubmit={onSubmit} noValidate>
              {mode === 'signup' ? (
                <Field
                  label="Your name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What should Oreia call you?"
                />
              ) : null}

              <Field
                label="Email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />

              {mode !== 'forgot' ? (
                <Field
                  label="Password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint={
                    mode === 'signup'
                      ? 'At least 8 characters. This also protects your encryption key, so choose something you will not lose.'
                      : undefined
                  }
                />
              ) : null}

              {mode === 'signup' ? (
                <Field
                  label="Invite code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Optional during open beta"
                  hint="Oreia is in private beta. If you have a code from your invitation, enter it here."
                />
              ) : null}

              {error ? <Notice tone="danger">{error}</Notice> : null}

              <PrimaryButton type="submit" loading={busy}>
                {mode === 'signup' ? 'Create my account' : mode === 'forgot' ? 'Send reset link' : 'Sign in'}
              </PrimaryButton>
            </form>
          )}

          {!sentReset && mode !== 'forgot' ? (
            <>
              <div className="orow" style={{ margin: '22px 0', gap: 14 }}>
                <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
                <span className="olabel">or</span>
                <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              </div>

              <a className="octa quiet" href={googleSignInURL()} style={{ textDecoration: 'none' }}>
                Continue with Google
              </a>
            </>
          ) : null}

          {!sentReset ? (
            <div
              className="ostack"
              style={{ gap: 10, marginTop: 26, fontSize: 13.5, color: 'var(--ink-mute)' }}
            >
              {mode === 'signin' ? (
                <>
                  <button className="olink-plain" type="button" onClick={() => setMode('signup')}>
                    New to Oreia? <strong>Create an account</strong>
                  </button>
                  <button className="olink-plain" type="button" onClick={() => setMode('forgot')}>
                    Forgot your password?
                  </button>
                </>
              ) : (
                <button className="olink-plain" type="button" onClick={() => setMode('signin')}>
                  Already have an account? <strong>Sign in</strong>
                </button>
              )}
            </div>
          ) : null}

          {/* Plain anchors, not <Link>: terms.html is a static file served by the CDN (see
              public/_redirects), so it must be a real navigation. The policy now lives on the
              marketing site — linked absolutely rather than through this host's /privacy redirect,
              so the reader gets the canonical URL without a hop. */}
          <p className="otext-mute" style={{ fontSize: 12, lineHeight: 1.6, marginTop: 30 }}>
            By continuing you agree to our <a href="/terms">Terms</a> and{' '}
            <a href="https://oreiathejournal.com/privacy">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
